import { Text, Link, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import clsx from 'clsx'
import React, { FunctionComponent, useEffect } from 'react'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import {
  setCreditCheckAgree,
  useAppDispatch,
  useCheckoutState,
  setTermsDisclosureAgree,
  openPPAModal,
  useIsAfterpayFinanceFeeEligible,
  setConfirmTermsViewed,
  useIsBillingAgreementApprovalProcess,
  useApplePaylaterLinkParams,
} from '../../state'
import { TermLink } from '../TermLink'
import styles from './style.module.css'
import { useGetTerms, useLateFeeDetails, useTermsEventProps, useIsUkRegulatedTerms } from './helper'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { Trans, useTranslation } from '../../utils/convergence'
import { PaymentAuthorizationLink } from '../PaymentAuthorizationLink'

export const Terms: FunctionComponent = () => {
  const { t } = useTranslation()
  const { instrument, logEvent } = useMonitoringWithEnduringEventProperties()
  const { lateFee } = useLateFeeDetails()
  const { started } = useCheckoutState()
  const terms = useGetTerms()
  const shouldUseUkRegulatedOutboundLinks = terms === 'summary:termsFCA_regulated'
  const termsEventProps = useTermsEventProps()
  const dispatch = useAppDispatch()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const isUkRegulated = useIsUkRegulatedTerms()
  const withApplePaylaterParams = useApplePaylaterLinkParams()
  const ukRegulatedCreditAgreementUrl = withApplePaylaterParams(t('common:termsOfServiceUrl'))
  const ukRegulatedCancelAnytimeUrl = withApplePaylaterParams(t('common:ukRegCancelAnytimeUrl'))
  const termsOfServiceUrl = withApplePaylaterParams(t('common:termsOfServiceUrl'))
  const privacyPolicyUrl = withApplePaylaterParams(t('common:privacyPolicyUrl'))
  const additionalProductInformationUrl = withApplePaylaterParams(t('common:additionalProductInformationUrl'))
  const cardAccountAgreementUrl = withApplePaylaterParams(t('summary:terms.cardAccountAgreementUrl'))
  const termsOfInstalmentAgreementUrl = withApplePaylaterParams(t('common:termsOfInstalmentAgreementUrl'))

  const onInstallmentAgreementClick = (): void => {
    if (shouldUseUkRegulatedOutboundLinks || isBillingAgreementApprovalProcess) {
      return
    }

    dispatch(openPPAModal())
  }

  useEffect(() => {
    dispatch(setCreditCheckAgree(termsEventProps.creditCheckConsent))
    dispatch(setTermsDisclosureAgree(termsEventProps.termsDisclosureAgree))
  }, [termsEventProps.creditCheckConsent, termsEventProps.termsDisclosureAgree])

  useEffect(() => {
    dispatch(setConfirmTermsViewed(terms))
  }, [terms])

  if (isAFF) {
    return null
  }

  const components = {
    terms: (
      <TermLink
        key='summary-terms-of-service'
        href={termsOfServiceUrl}
        color={linkColor}
        fontFace='CashSansMono'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_TERMS_LINK, { outboundLink: termsOfServiceUrl })}
      />
    ),
    privacyPolicy: (
      <TermLink
        key='summary-privacy-policy'
        href={privacyPolicyUrl}
        color={linkColor}
        fontFace='CashSansMono'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_PRIVACY_POLICY_LINK, { outboundLink: privacyPolicyUrl })}
      />
    ),
    installmentAgreement: (
      <Link
        target={shouldUseUkRegulatedOutboundLinks || isBillingAgreementApprovalProcess ? '_blank' : undefined}
        href={shouldUseUkRegulatedOutboundLinks ? ukRegulatedCreditAgreementUrl : isBillingAgreementApprovalProcess ? termsOfInstalmentAgreementUrl : undefined}
        kind='Underline'
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='ppa-link-installment'
        key='summary-instalment-agreement'
        onClick={
          shouldUseUkRegulatedOutboundLinks
            ? () => logEvent(TrackingEvent.PRESSED_SUMMARY_INSTALMENT_AGREEMENT_LINK, { outboundLink: ukRegulatedCreditAgreementUrl })
            : instrument(TrackingEvent.PRESSED_SUMMARY_INSTALMENT_AGREEMENT_LINK, onInstallmentAgreementClick)
        }
      />
    ),
    paymentAuthorization: shouldUseUkRegulatedOutboundLinks ? (
      <Link
        target='_blank'
        href={ukRegulatedCancelAnytimeUrl}
        kind='Underline'
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='ppa-link-payment'
        key='summary-payment-authorization'
        onClick={() => logEvent(TrackingEvent.PRESSED_SUMMARY_PAYMENT_AUTHORISATION_LINK, { outboundLink: ukRegulatedCancelAnytimeUrl })}
      />
    ) : (
      <PaymentAuthorizationLink color={linkColor} />
    ),
    additionalProductInformation: (
      <TermLink key='summary-additional-product-information' href={additionalProductInformationUrl} color={linkColor} fontFace='CashSansMono' />
    ),
    cardAccountAgreement: (
      <Link
        kind='Underline'
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='card-account-agreement'
        key='summary-card-account-agreement'
        target='_blank'
        href={cardAccountAgreementUrl}
      />
    ),
  }

  const values = {
    lateFee,
    otbAmount: started?.otbAmount?.amount,
  }

  return (
    <>
      <div className={clsx(designLanguageStyles.wrapper, isUkRegulated && styles.ukRegulatedWrapper)}>
        <MaskedElement actionName='Terms And Conditions'>
          <Text size='XXS' color='Gray40' testNameSpace='terms-conditions' fontFace='CashSansMono'>
            <Trans i18nKey={terms} components={components} values={values} />
          </Text>
        </MaskedElement>
        <TrackOnMount eventName={TrackingEvent.VIEWED_CHECKOUT_SUMMARY_TERMS} eventProperties={{ ...termsEventProps, termsViewed: terms }} />
      </div>
    </>
  )
}
